"use client";

import { SignupGate } from "./SignupGate";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { ACCOUNT_LINKS, accountsUrl } from "@/lib/account-links";
import { isSignedIn, useCartStore } from "@/lib/cart-store";

import type {
  SignupCredentials,
  SignupDetails,
  SignupProfileSelection,
  SignupStep,
} from "@repo/ui/lib/signup-funnel";
// S35 · F234 — the design-system icon set. `@repo/ui/icons` is the package's declared icon entry point
// (package.json exports "./icons"), which re-exports lucide-react. Importing through it rather than from
// lucide directly is what makes "icons from the design-system set" true structurally and not by habit.
import {
  CircleHelp,
  Gift,
  Handshake,
  Heart,
  LogIn,
  LogOut,
  Route,
  ShoppingCart,
  Smile,
  Ticket,
  X as XIcon,
} from "@repo/ui/icons";
import {
  canLeaveStep,
  emptyProfileSelection,
  nextSignupStep,
  prevSignupStep,
  SIGNUP_ACCOUNT_TYPES,
  SIGNUP_STEP_LABELS,
  SIGNUP_STEPS,
  SIGNUP_TAXONOMY,
  signupStepIndex,
  stepBlockers,
} from "@repo/ui/lib/signup-funnel";

import { AgentPartnerModal, MembershipModal } from "./InfoModals";
import { MemberAvatarImg } from "./MemberAvatar";
import {
  SignupBlockedReason,
  SignupCredentialsFields,
  SignupDetailsFields,
} from "./SignupFields";

// Card 86bb671n4 — the funnel's step order, field rules and (PENDING-FOUNDER) tier taxonomy. One
// module, shared with the accounts app's /sign-up page, so the two surfaces cannot drift.

// W6 (Phase-1b) — the global chrome SIGN-IN popup ("login popup-not-page" per the constitution §0f).
// PARITY: this reproduces the LIVE snowbus.com login modal 1:1 — the legacy `src/modals/Login` react-bootstrap
// Modal ("loginPopup mountainPopup modal-sm"): a #fdfdfd card with a 2px #cbe0e4 border, EMAIL*/PASSWORD*
// placeholder inputs, a Remember-Me + Forgot-Password row, an outline LOGIN button, an "Or sign in with:"
// divider, the two social pills (fb.svg/gp.svg, ported verbatim — SINCE REMOVED, see §27.A at the social
// block below: live still renders them, we deliberately do not), the terms/privacy small-print, and the
// "SIGN UP! ITS FREE" → filled "NEW sign up" button. Tokens are live's compiled SCSS values (styles/_variable
// .scss: $secondaryOpacity #cbe0e4, $primaryColor #828282, $orangeColor #f15a22, $primaryFont Roboto).
//
// The EMAIL/PASSWORD path is REAL — wired to the existing same-origin /api/auth/login proxy through the shared
// cart-store session, so chrome sign-in and the cart sign-in are ONE session (never transacts against live: the
// proxy targets the LOCAL Strapi). Social OAuth (Google/Facebook) + the "NEW sign up" registration flow are
// FOUNDER RAILS (Auth0 / accounts registration), not wired in the port — rendered for appearance parity the
// same way maps live-tiles (F37) are gated; the parts that are real are the parts the founder's W6 walk covers.
// Harness hooks: data-auth-*.
// ACCOUNT-MENU-SINGLE-20260925 — this is the older R8 overlay: it stays for the host chrome's auth forms and for
// non-export routes. The design's own Account button opens the Kit 2.01 panel instead (see ExportSessionBridge).
export default function LoginPopup() {
  const authOpen = useCartStore((s) => s.authOpen);
  const setAuthOpen = useCartStore((s) => s.setAuthOpen);
  // S21 · F134 — the funnel opens this popup straight on the login form (guest clicks a MEMBER price),
  // while the profile icon still opens the guest menu first. authView carries which face to show.
  const authView = useCartStore((s) => s.authView);
  const session = useCartStore((s) => s.session);
  const signIn = useCartStore((s) => s.signIn);
  const signOut = useCartStore((s) => s.signOut);
  // Customer SSO (off unless the server says so): the first credential prompt stays in THIS popup, on the page the visitor is on;
  // only the transport changes (provider interaction API, same origin). Feature off, `signIn` below is the unchanged legacy path.
  const bootstrapSso = useCartStore((s) => s.bootstrapSso);
  const signInSso = useCartStore((s) => s.signInSso);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // S12 · RW2-4 — signed-out, the profile icon opens live's MENU first; the login modal appears only after
  // "Login / Sign Up" is chosen. Reset whenever the popup closes so the next click starts at the menu again.
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false); // F147
  // S36 card 2 · item 5 — the reference (FIGMA-REFS-S36/sign-in-up.png) names THREE panels: Step 1 sign
  // up, LOGIN, and Reset Password. The first two were ported; this is the third. It is a VIEW of this
  // same modal rather than a route, because the card's item 5 is "login opens a page, not a popup" —
  // sending the popup's own control to the accounts app's /forgot-password PAGE would rebuild that
  // defect at the moment we claim to close it.
  const [showReset, setShowReset] = useState(false);
  // Card 86bb671n4 — the membership-info popup, opened from the funnel's ⓘ (the render draws it beside
  // "Join Now, it's Free!") and again from the confirmation panel. MembershipModal already existed and
  // was already built to live (F128/F222); it was IMPORTED here and never rendered — the funnel is the
  // point the design shows it at, and this is that wiring.
  const [showMembership, setShowMembership] = useState(false);
  // Card 86bb671n4 — the funnel's CONFIRMATION panel would otherwise be unreachable. `signUp` sets the
  // session BEFORE it resolves (cart-store), so the `signedIn` early return below swapped this popup for
  // the account menu while `await onSubmit(...)` was still suspended inside SignupFunnel: the funnel
  // unmounted and its `setCreated` landed on a dead component. Nobody ever saw "You're in the club!".
  // The flag lives HERE, above that early return, so the confirmation the design draws survives the
  // sign-in it is confirming. Cleared with the rest of the panel state when the popup closes.
  //
  // ⚠ IT HOLDS THE EMAIL, NOT A BOOLEAN, AND THAT IS THE WHOLE FIX (measured 2026-07-30, browser).
  // A boolean flag here was necessary and NOT sufficient: it correctly kept this popup on the funnel,
  // but the funnel had already unmounted, so React MOUNTED A FRESH ONE — `useState(null)` again, no
  // `created`, straight back to step one. Register returned 200 and the visitor saw the empty form:
  // exactly the fabricated-outcome shape this estate keeps finding, only inverted (a real success that
  // renders as nothing). State that must survive a remount cannot live in the thing that remounts, so
  // the address lives here and is passed back DOWN as `createdEmail`.
  const [signupCreated, setSignupCreated] = useState<string | null>(null);
  const signUp = useCartStore((s) => s.signUp);
  useEffect(() => {
    if (!authOpen) {
      setShowLogin(false);
      setShowSignup(false);
      setShowReset(false);
      setShowMembership(false);
      setSignupCreated(null);
    }
    // F134 — a login-intent open (funnel member-price click) skips the guest menu.
    else if (authView === "login") setShowLogin(true);
    // Youth spec v1.1: every sign-up entry (the exported design's included) opens here, on the gated NEW SIGN UP.
    else if (authView === "signup") setShowSignup(true);
  }, [authOpen, authView]);
  // Learn once, from the server, whether SSO is on, and adopt/refresh a verified cookie-backed session when it is.
  useEffect(() => {
    if (authOpen) void bootstrapSso();
  }, [authOpen, bootstrapSso]);
  // RW-8 (S11) — where the profile trigger sits, so the SIGNED-IN menu can anchor to it. Every trigger on
  // every header variant (home / content / hub / the captured chrome wired by HubChromeLayer) carries
  // data-behavior="signin-open", so one capture-phase listener positions the menu on all of them.
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = (e.target as HTMLElement | null)?.closest?.(
        '[data-behavior="signin-open"]',
      );
      if (!t) return;
      // Prefer the VISIBLE icon inside the trigger. Several ported triggers (`.clickProfile`) are zero-width
      // wrappers whose <img> is what actually paints and is positioned elsewhere, so measuring the wrapper
      // would anchor the menu to the wrong place.
      const el = t as HTMLElement;
      let box: HTMLElement = el;
      if (el.getBoundingClientRect().width === 0) {
        const icon = el.querySelector<HTMLElement>("img, svg");
        if (icon && icon.getBoundingClientRect().width > 0) box = icon;
      }
      const r = box.getBoundingClientRect();
      // Ignore HIDDEN triggers: the hamburger's "Guest User" row is also a signin-open trigger but lives in a
      // display:none dropdown, so its rect is ALL zeros — anchoring to it would pin the menu to the top-left
      // corner instead of the profile icon. Test for a fully-unlaid-out box (not merely zero-width: the real
      // `.clickProfile` wrapper measures 0 wide × 24 high around its icon but is correctly positioned).
      if (r.width === 0 && r.height === 0) return;
      setAnchor({ x: r.right, y: r.bottom });
    };
    document.addEventListener("click", onDown, true);
    return () => document.removeEventListener("click", onDown, true);
  }, []);

  useEffect(() => {
    if (!authOpen) return;
    const onKey = (e: KeyboardEvent) => {
      // Card 86bb671pf — this handler used to tear the whole dialog down while the membership-info modal
      // sat open on top of the funnel, unmounting SignupFunnel and discarding the visitor's input. It is
      // unchanged: the fix is that ModalShell now consumes Escape in the window capture phase, so this
      // never runs while a modal layer is up. See the block comment there — a guard here was tried first
      // and could not work, because this component is mounted more than once per page.
      if (e.key === "Escape") setAuthOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [authOpen, setAuthOpen]);

  if (!authOpen) return null;
  const signedIn = isSignedIn(session);

  // RW-8 — FOUNDER RE-WALK: "the signed-in user icon should be a DROPDOWN, not the login popup; only
  // signed-OUT click opens the popup". The signed-in panel's CONTENT is unchanged (Signed in as … + the
  // account link-out rows + SIGN OUT) — it is simply presented as a menu anchored to the profile icon
  // instead of a centred modal. Signed-out keeps the live-parity modal exactly as ported (W6).
  // `&& !signupCreated` — a sign-up that just succeeded IS signed in, but the funnel's confirmation is
  // the last step of the flow the founder walks. Without this the account menu replaces it instantly.
  if (signedIn && !signupCreated) {
    return (
      <AccountMenu
        anchor={anchor}
        email={session.email}
        onClose={() => setAuthOpen(false)}
        onSignOut={() => {
          signOut();
          setAuthOpen(false);
        }}
      />
    );
  }

  // S12 · RW2-4 — FOUNDER: "the profile window doesn't size properly". Evidence first, per the WO: live vs
  // build captured at 1440×900 on /mountain-whistler/profile, same interaction. The divergence is not a
  // sizing bug at all — it is the WRONG PANEL:
  //   live  → an anchored menu, 200×196 at (1224,64), items: Login / Sign Up · My Trips · Member Perks · FAQ
  //   build → the 420×618 login modal, centred at (510,141)
  // S11's RW-8 work gave the SIGNED-IN state an anchored menu but left signed-out opening the modal
  // directly, so the founder met a large centred dialog where live shows a small corner menu.
  //
  // Live's own menu (read off the DOM, hrefs included) is reproduced here, and "Login / Sign Up" opens the
  // already-ported W6 modal — which stays exactly as it was, just one step later in the flow.
  if (!signedIn && !showLogin) {
    return (
      <GuestMenu
        anchor={anchor}
        onClose={() => setAuthOpen(false)}
        onLogin={() => setShowLogin(true)}
      />
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const sso = await bootstrapSso();
    const r = sso ? await signInSso(email.trim(), password) : await signIn(email.trim(), password);
    setBusy(false);
    if (r.ok) {
      setPassword("");
      setAuthOpen(false);
    } else {
      setError(r.error || "Invalid email or password");
    }
  };

  const TEAL = "#71abb8";
  const BORDER = "#cbe0e4"; // $secondaryOpacity
  const GREY = "#828282"; // $primaryColor
  const ORANGE = "#f15a22"; // $orangeColor

  const field: React.CSSProperties = {
    width: "100%",
    height: "53px",
    padding: "0 16px",
    borderRadius: "6px",
    border: `1.5px solid ${BORDER}`,
    fontSize: "13px",
    fontFamily: "inherit",
    letterSpacing: "0.5px",
    color: "#4b5563",
    boxSizing: "border-box",
    outline: "none",
    marginBottom: "16px",
  };

  return (
    <div
      data-auth-overlay
      onClick={(e) => {
        if (e.target === e.currentTarget) setAuthOpen(false);
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        data-auth-dialog
        role="dialog"
        aria-modal="true"
        aria-label="Sign in"
        className="loginPopup mountainPopup"
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "#fdfdfd",
          borderRadius: "8px",
          border: `2px solid ${BORDER}`,
          boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          padding: "20px 15px 22px 21px",
          position: "relative",
          fontFamily: '"Roboto", system-ui, sans-serif',
          // Card 86bb671pf — WITHOUT THESE TWO LINES THE SIGN-UP FUNNEL DEAD-ENDS ON SHORT VIEWPORTS.
          // Measured at the account-type step (signup-back-probe.mjs): the dialog is 988px tall, and
          // the overlay above centres it with
          // `align-items:center` inside `position:fixed; inset:0`. A centred flex item taller than its
          // container overflows EQUALLY IN BOTH DIRECTIONS — the dialog's top goes negative (-44 @900,
          // -144 @700) and its bottom runs past the fold — and because the overlay is fixed, scrolling
          // the document cannot recover it. Playwright reported the Continue button "visible, enabled
          // and stable" and then "outside of the viewport" through 58 retries: an EXISTENCE assertion
          // passes on that button while a user can never click it, which is S29's lesson exactly.
          // WHAT WAS ACTUALLY OBSERVED, stated as measured rather than as inferred from geometry —
          // the two differ here and the difference is the honest part. By GEOMETRY the Continue button
          // hangs past the fold from h=800 (+39px). By REAL CLICK it still lands at 800 and only becomes
          // genuinely unclickable at h=740 (+69px) and h=700 (+89px), which is what the break-on-purpose
          // revert reddened on. The Back button is the one lost at h=900 (+20px), and that is why the
          // pre-existing step check caught anything at all. So: "no 1440x800 laptop can sign up" would
          // have been an over-claim; the true statement is that Back dies at 900 and Continue at 740.
          // Step 2 became tall enough to trip this when 07e6f82 correctly started drawing all 19 tiers
          // unconditionally; the height was the latent trap, not that fix. `maxHeight:100%` clamps the
          // item to the overlay so it can never overflow past centre, and `overflowY:auto` gives the
          // funnel its own scroll instead.
          maxHeight: "100%",
          overflowY: "auto",
        }}
      >
        {/* modal-header closeButton — absolute top-right (live: right 10 / top 10) */}
        <button
          type="button"
          aria-label="Close"
          onClick={() => setAuthOpen(false)}
          style={{
            position: "absolute",
            top: "8px",
            right: "10px",
            border: 0,
            background: "none",
            fontSize: "22px",
            lineHeight: 1,
            cursor: "pointer",
            color: GREY,
          }}
        >
          ×
        </button>

        {showReset ? (
          // S36 card 2 · item 5 — the reference's third panel. Checked BEFORE showSignup so the reset
          // view wins while it is open; "create new account? Sign Up" hands off to the signup panel.
          <ResetPasswordForm
            field={field}
            grey={GREY}
            orange={ORANGE}
            onBack={() => setShowReset(false)}
            onSignUp={() => {
              setShowReset(false);
              setShowSignup(true);
            }}
          />
        ) : showSignup ? (
          // F147 — NEW SIGN UP now opens a real sign-up form (fields + validation = the FastCurve
          // Techno-Functional "Signup" contract), wired end-to-end to the local Strapi via cart-store.signUp.
          // Card 86bb671n4 — that form is now the three-step funnel, not one box.
          // Youth spec v1.1 (founder 2026-10-20 21:14 + 21:29): the age step and the required agreement come first (SignupGate), and every
          // account the funnel creates carries them.
          <SignupGate<{ firstName: string; lastName: string; email: string; phone: string; password: string }> orange={ORANGE} grey={GREY} onCancel={() => setShowSignup(false)} signUp={signUp}>
            {(onGatedSubmit) => (
              <SignupFunnel
                onCancel={() => setShowSignup(false)}
                onSubmit={onGatedSubmit}
                onCreated={(email) => setSignupCreated(email)}
                createdEmail={signupCreated}
                onDone={() => setAuthOpen(false)}
                onMembershipInfo={() => setShowMembership(true)}
              />
            )}
          </SignupGate>
        ) : (
          <>
            {/* LoginViaEmail — the real path (Formik on live; wired to /api/auth/login here) */}
            <form
              data-auth-form
              onSubmit={submit}
              style={{ paddingTop: "30px", width: "100%" }}
            >
              <input
                type="email"
                autoComplete="email"
                required
                placeholder="EMAIL*"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={field}
              />
              <input
                type="password"
                autoComplete="current-password"
                required
                placeholder="PASSWORD*"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{ ...field, marginBottom: "10px" }}
              />
              {error && (
                <p
                  data-auth-error
                  style={{
                    color: "#dc2626",
                    fontSize: "13px",
                    margin: "0 0 8px",
                  }}
                >
                  {error}
                </p>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                    color: GREY,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    id="rememberMe"
                    style={{ accentColor: ORANGE }}
                  />
                  Remember Me
                </label>
                {/* S36 card 2 · item 5 — was a bare <span> with cursor:pointer and NO handler: a control
                    that advertised a destination which did not exist. That is the dead-affordance class
                    this estate already paid for at F172/F275, and it is why the new gate CLICKS this
                    rather than asserting it renders. `type="button"` matters — it sits inside the login
                    <form>, so a default-type button would SUBMIT the sign-in on click. */}
                <button
                  type="button"
                  data-auth-forgot-open
                  onClick={() => {
                    setError(null);
                    setShowReset(true);
                  }}
                  style={{
                    fontSize: "13px",
                    color: ORANGE,
                    cursor: "pointer",
                    border: 0,
                    background: "none",
                    padding: 0,
                    fontFamily: "inherit",
                  }}
                >
                  Forgot Password?
                </button>
              </div>
              <button
                type="submit"
                disabled={busy}
                style={{
                  display: "block",
                  width: "100%",
                  padding: "13px",
                  borderRadius: "6px",
                  border: `1.5px solid ${ORANGE}`,
                  background: "#fff",
                  color: ORANGE,
                  fontWeight: 600,
                  fontSize: "15px",
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  cursor: busy ? "default" : "pointer",
                  opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? "Signing in…" : "Login"}
              </button>
            </form>

            {/* WO-FOUNDER-WALK-UNBLOCK-001 · T5 — the "Or sign in with:" divider and the two social
                pills (fb.svg / gp.svg) are REMOVED. Founder ruling 2026-08-12 (§27.A), verbatim: "I agree
                with removing Facebook, Apple, and Google for now and just doing a login because we haven't
                set that up." They were rendered "for appearance parity" and were never wired — the exact
                shape of the defect he found first (a control that cannot work is worse than none). The
                email/password form above is real. Real SSO returns as backend scope (WO-B14). */}

            {/* xsText — terms/privacy small print (links via the /terms, /privacy 301s in REDIRECT_MAP) */}
            <small
              style={{
                display: "block",
                width: "72%",
                margin: "6px auto 0",
                textAlign: "center",
                fontSize: "9px",
                lineHeight: 1.5,
                color: GREY,
              }}
            >
              By continuing, you are indicating that you accept our{" "}
              <a href="/terms" style={{ color: ORANGE }}>
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="/privacy" style={{ color: ORANGE }}>
                Privacy Policy
              </a>
            </small>

            {/* sign up! its free → NEW sign up (registration = founder rail; appearance ported) */}
            <span
              style={{
                display: "block",
                textAlign: "center",
                color: GREY,
                fontSize: "15px",
                fontWeight: 500,
                textTransform: "uppercase",
                marginTop: "20px",
                marginBottom: "12px",
              }}
            >
              sign up! its free
            </span>
            <button
              type="button"
              data-auth-signup-open
              onClick={() => setShowSignup(true)}
              style={{
                display: "block",
                width: "237px",
                height: "50px",
                margin: "0 auto",
                borderRadius: "6px",
                border: 0,
                background: ORANGE,
                color: "#fff",
                fontWeight: 600,
                fontSize: "15px",
                textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              New sign up
            </button>
          </>
        )}
      </div>

      {/* Card 86bb671n4 — the membership-info popup. Rendered as a SIBLING of the auth dialog, inside
          the same overlay, rather than replacing it: ModalShell is fixed at z-index 3000 so it paints
          above, and the funnel stays MOUNTED underneath. That matters — swapping it in would unmount
          SignupFunnel and silently discard everything the visitor had typed, which is the sort of
          "it looked fine" defect this estate keeps paying for. Dismissing it returns to the exact
          step it was opened from because that step never went away. */}
      {showMembership && (
        <MembershipModal onClose={() => setShowMembership(false)} />
      )}
    </div>
  );
}

// S36 card 2 · item 5 — RESET PASSWORD, the third panel of FIGMA-REFS-S36/sign-in-up.png.
//
// The reference is a 2023-era FRAMEWORK reference (the card says so explicitly), so what is taken from it
// is the STRUCTURE it fixes — centred "Reset Password" heading, one required EMAIL field, a filled orange
// Submit, and a "create new account? Sign Up" footer — while the styling authority stays the design
// system, i.e. the same $variable tokens (`field`, GREY, ORANGE) the rest of this modal already renders
// with. Nothing here is a new visual language.
//
// ONE DELIBERATE DIVERGENCE FROM THE REFERENCE, stated rather than left implied: the reference draws its
// Submit as an OUTLINE button (white fill, orange border and text) matching its own LOGIN button. This
// renders it FILLED orange instead, matching the filled "New sign up" CTA this modal already ships. The
// card makes the design system the styling authority over a 2023-era framework reference, and in the
// built modal the outline treatment is the SECONDARY action while the panel's only action should read as
// primary. Recorded because "built to the reference" must not quietly mean "built to most of it".
//
// NO INVENTED SUCCESS. Strapi's forgot-password actually sends mail, so it can fail for reasons that have
// nothing to do with the address the visitor typed. This form renders exactly one of two real outcomes:
// the confirmation IFF the proxy returned ok, or the REAL error string it returned. It never shows a
// reassuring "check your inbox" over a failed request — that would be a fabricated success, and it is the
// single easiest place in an auth flow to ship one.
function ResetPasswordForm({
  field,
  grey,
  orange,
  onBack,
  onSignUp,
}: {
  field: React.CSSProperties;
  grey: string;
  orange: string;
  onBack: () => void;
  onSignUp: () => void;
}) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
      };
      if (res.ok && json.ok) setSent(true);
      else setError(json.error || "Could not send the reset email");
    } catch (err) {
      setError(err instanceof Error ? err.message : "network");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-auth-reset style={{ paddingTop: "18px", width: "100%" }}>
      <h2
        style={{
          margin: "0 0 18px",
          textAlign: "center",
          fontSize: "17px",
          fontWeight: 700,
          color: "#4b5563",
        }}
      >
        Reset Password
      </h2>

      {sent ? (
        // Reached ONLY on a real ok from the proxy.
        <p
          data-auth-reset-sent
          style={{
            fontSize: "13px",
            color: grey,
            textAlign: "center",
            margin: "0 0 18px",
          }}
        >
          If that email is registered, a password reset link is on its way.
        </p>
      ) : (
        <form data-auth-reset-form onSubmit={submit}>
          <input
            type="email"
            autoComplete="email"
            required
            aria-label="Email"
            placeholder="EMAIL*"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={field}
          />
          {error && (
            <p
              data-auth-reset-error
              style={{ color: "#dc2626", fontSize: "13px", margin: "0 0 12px" }}
            >
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            style={{
              display: "block",
              width: "237px",
              height: "50px",
              margin: "0 auto",
              borderRadius: "6px",
              border: 0,
              background: orange,
              color: "#fff",
              fontWeight: 600,
              fontSize: "15px",
              cursor: busy ? "default" : "pointer",
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? "Sending…" : "Submit"}
          </button>
        </form>
      )}

      {/* reference footer: "create new account? Sign Up" */}
      <p
        style={{
          textAlign: "center",
          fontSize: "13px",
          color: grey,
          margin: "20px 0 0",
        }}
      >
        create new account?{" "}
        <button
          type="button"
          data-auth-reset-signup
          onClick={onSignUp}
          style={{
            border: 0,
            background: "none",
            padding: 0,
            color: orange,
            textDecoration: "underline",
            cursor: "pointer",
            fontSize: "13px",
            fontFamily: "inherit",
          }}
        >
          Sign Up
        </button>
      </p>

      {/* Not in the 2023 reference, and deliberately added: the reference panel is a standalone page-like
          modal, but here it is a VIEW of the sign-in modal, so without this the visitor can reach reset
          and then has no way back to LOGIN except closing and reopening. */}
      <button
        type="button"
        data-auth-reset-back
        onClick={onBack}
        style={{
          display: "block",
          margin: "14px auto 0",
          border: 0,
          background: "none",
          padding: 0,
          color: grey,
          fontSize: "13px",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        Back to login
      </button>
    </div>
  );
}

// S12 · RW2-4 — the SIGNED-OUT profile menu, live-derived. Live renders an anchored MUI menu (measured
// 200×196, right-aligned under the profile icon) whose rows are exactly:
//   • "Login / Sign Up"  — a button, opens the login modal (NOT a link)
//   • "My Trips"         → /trips
//   • "Member Perks"     → /mountain-<code>/snowclub-perks   (live served the per-mountain page)
//   • "FAQ"              → /faq
// Perks resolves per-mountain only for Whistler, which is the only mountain whose perks page the build
// serves (same rule as the RW2-8 tab); everywhere else it falls back to the served global /snowclub-perks,
// so no row can 404. Styling matches the RW-8 signed-in menu so the two states read as one system.
function GuestMenu({
  anchor,
  onClose,
  onLogin,
}: {
  anchor: { x: number; y: number } | null;
  onClose: () => void;
  onLogin: () => void;
}) {
  useEffect(() => {
    // Card 86bb671pf — the second route into the same defect: this menu's onClose is `setAuthOpen(false)`,
    // and the DUPLICATE LoginPopup mount sits here (guest menu) while the other mount shows the funnel, so
    // Escape could close the shared flag from the side. Also covered by ModalShell's capture-phase
    // consumption — the key never reaches this listener while a modal is open.
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const TEAL = "#71abb8";
  const BORDER = "#cbe0e4";
  // S35 · F234 — the guest panel follows the ruled menu's width. See AUTH_MENU_ROW's note: the mockup
  // draws the signed-in state, but F235 is what keeps the two states one menu, so the ruling is
  // applied to both rather than to the drawn one alone.
  const WIDTH = 361;
  const vw =
    (typeof window !== "undefined" &&
      (window.innerWidth || document.documentElement?.clientWidth)) ||
    0;
  const fallbackLeft = vw > 0 ? vw - WIDTH - 16 : 16;
  const right = anchor ? anchor.x - WIDTH : fallbackLeft;
  const left =
    vw > 0 ? Math.max(8, Math.min(right, vw - WIDTH - 8)) : Math.max(8, right);
  const top = anchor ? anchor.y + 8 : 64;

  const code =
    typeof location !== "undefined"
      ? (/^\/mountain-([a-z0-9-]+?)(?:\/|$)/i.exec(location.pathname)?.[1] ??
        null)
      : null;
  const perksHref =
    code === "whistler"
      ? "/mountain-whistler/snowclub-perks"
      : "/snowclub-perks";
  // F234 — icons from the same design-system set the ruled menu uses, so the two states are one menu
  // in artwork as well as in scale. The DESTINATIONS are untouched: they remain the live-derived hrefs
  // above, and no row gained or lost a route here.
  const rows: { label: string; href: string; icon: typeof Smile }[] = [
    { label: "My Trips", href: "/trips", icon: Route },
    { label: "Member Perks", href: perksHref, icon: Gift },
    { label: "FAQ", href: "/faq", icon: CircleHelp },
  ];

  // S35 · F235 — the SHARED row scale, not a second copy of the numbers. See AUTH_MENU_ROW.
  const rowStyle = AUTH_MENU_ROW;

  return (
    <div
      data-auth-overlay
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "transparent",
      }}
    >
      <div
        data-auth-guestmenu
        data-auth-menu
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          left,
          top,
          width: WIDTH,
          background: "#fdfdfd",
          // F234 — same card treatment as the ruled signed-in panel (F235: one menu, two states)
          border: `1px solid ${DIVIDER}`,
          borderRadius: "12px",
          boxShadow: "0 12px 28px rgba(0,0,0,.18)",
          padding: "10px 6px",
          fontFamily: '"Roboto", system-ui, sans-serif',
          display: "flex",
          flexDirection: "column",
        }}
      >
        <button
          type="button"
          data-auth-login-open
          data-auth-menu-row
          onClick={onLogin}
          style={rowStyle}
        >
          <LogIn {...AUTH_MENU_ICON} style={{ flex: "0 0 auto" }} aria-hidden />
          Login / Sign Up
        </button>
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <a
              key={r.href}
              data-auth-menu-link
              data-auth-menu-row
              href={r.href}
              style={rowStyle}
            >
              <Icon
                {...AUTH_MENU_ICON}
                style={{ flex: "0 0 auto" }}
                aria-hidden
              />
              {r.label}
            </a>
          );
        })}
      </div>
    </div>
  );
}

// RW-8 (S11) — the SIGNED-IN account menu: an anchored dropdown rather than a centred modal. The content is
// F119 — live's signed-in menu, item-for-item, in live's order (reference 2026-07-23).
// `href: null` = deliberately inert, per the reference's §3: Saved Items has no surface in this build,
// and Buy & Sell is dead on live too (RW2-8). Glyphs stand in for live's outline icon set — the icons
// themselves are not in the capture, so the LABELS and ORDER are what is being matched here.
const MENU_ITEMS: {
  label: string;
  // S35 · F235 — the `glyph` emoji stand-ins are GONE, not restyled. See AUTH_MENU_ROW.
  // S35 · F234 — REAL ARTWORK REPLACES THEM, on the founder's ruling. These are lucide components
  // from `@repo/ui/icons`, chosen to the mockup's drawings one for one; nothing is a stand-in now.
  icon: typeof Smile;
  href: string | null;
  external?: boolean;
  /** F127 — opens a dialog instead of navigating */
  modal?: "agent";
}[] = [
  { label: "Profile", icon: Smile, href: accountsUrl("/account") },
  { label: "My Purchases", icon: Ticket, href: accountsUrl("/my-orders") },
  { label: "Saved Items", icon: Heart, href: null },
  // V5: "Invite A Friend" (its /refer-friends route does not exist) and "MySnowClub.Com" (the legacy external brand exit)
  // are obsolete and removed here as in the design's own account panel; nothing is rerouted to login or membership.
  { label: "Buy & Sell", icon: ShoppingCart, href: null },
  // F127 — live opens a MODAL here, not a navigation (founder screenshot 2026-07-23). The S16
  // F119 build pointed this at /general-information#become-an-agent, which was my inference from
  // the anchor existing — wrong. `modal: true` routes it to the business-signup dialog below.
  { label: "Agent & Partner", icon: Handshake, href: null, modal: "agent" },
];

// ── S35 · F234 / F235 — ONE ROW SCALE FOR BOTH STATES OF THE USER DROPDOWN ────────────────────────
//
// Founder walk 2026-07-27: F234 "the user dropdown is not true to form — its icons, fonts and sizing
// do not match"; F235 "signed-out and logged-in diverge from each other too — one menu component,
// two content states, identical icons/fonts/sizing/structure in both".
//
// MEASURED, and he is right on F235 — three different scales were being served:
//     hamburger              Roboto 20px/300 · row h 50 · pad 10px 20px 10px 34px · real <img> per row
//     user dropdown OUT      Roboto 13px/600 · row h 38 · pad 8px 10px            · no icon
//     user dropdown IN       Roboto 15px/400 · row h 41 · pad 9px 4px             · EMOJI glyphs
//
// THIS CONSTANT IS THE ONE SOURCE, and it is the SIGNED-OUT scale, not a new invention: that panel is
// a measured port of live's own profile menu (200×196, recorded at the guest-menu render), so it is
// the only user-dropdown scale on this surface with a live reference behind it. The signed-in menu now
// consumes the identical object rather than a parallel one, which is what makes F235 structural
// instead of a coincidence of numbers that can drift apart again.
//
// THE EMOJI ARE GONE. They were honestly labelled a stand-in ("Glyphs stand in for live's outline icon
// set — the icons themselves are not in the capture"), but a stand-in for real artwork is the exact
// thing the no-invented-data rail deletes — same call S28 made on the fabricated vendor contact links
// and S30 made on the vendor logo. Live's own signed-out profile menu carries NO icons, so no icon in
// either state is both the honest render and the one that satisfies "identical in both".
//
// ── ⚠ THE ROUTED HALF CAME BACK RULED — FOUNDER RULING 2026-07-28 ─────────────────────────────────
// 7d2a019 routed "match the HAMBURGER's icons/fonts/sizing" rather than guessing it, because matching
// the hamburger would have meant knowingly diverging from live's measured 200×196 icon-less panel.
// The founder answered with something better than a pick between the two: a MOCKUP of the menu he
// wants — `F234-ACCOUNT-MENU-RULING-2026-07-28.png` in the Build Kit root. So neither of the two
// existing scales wins; the mockup is now the source, and a founder-supplied reference outranks a
// reconstruction from the mirror. THE ROUTE IS CLOSED, and it was closed by a ruling, not by a
// default I invented.
//
// MEASURED OFF THE MOCKUP (763×1098, a 2× crop — every figure below is the halved 1× value):
//     row pitch   90 → 45px          icon box    44 → 22px
//     label       ~38 → 17px/400     avatar     102 → 51px
//     panel      723 → ~361px wide   close box   52 → 26px
// The label colour stays the live $primaryColor #828282 already in this file rather than a fresh
// sample off a PNG: it is the token the rest of this chrome is built from and it reads as the
// mockup's grey. DIVIDER is the one genuinely new value — the mockup's rules are neutral grey, not
// the teal-tinted #cbe0e4 border, and pretending otherwise would be matching the old panel instead
// of the ruling.
//
// F235 STILL BINDS, AND THIS IS THE POINT OF THE CONSTANT: "one menu component, two content states,
// identical icons/fonts/sizing/structure in both". The mockup only draws the SIGNED-IN state, so the
// naive read is to restyle that one and leave the guest menu on 13px/600 — which would re-open the
// exact divergence F235 was raised about, one sprint after closing it. Both states consume this
// object, and the guest rows take icons from the same lucide set, so applying the ruling to the menu
// the founder drew does not silently un-apply the finding he raised before it. Overridable: if the
// guest menu is meant to stay on live's icon-less 200×196 panel, that is a one-line revert here.
const DIVIDER = "#e6e6e6"; // mockup's neutral rule; #cbe0e4 is the teal panel border, not this
const AUTH_MENU_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  // 11px vertical on a 22px icon lands the row on the mockup's measured 45px pitch
  padding: "11px 12px",
  borderRadius: "8px",
  // the bordered teal pill is GONE — the mockup's rows are plain, separated by space rather than by
  // a box. Kept as explicit `none` so a stylesheet cannot quietly put a border back.
  border: "none",
  background: "#fff",
  color: "#828282",
  fontWeight: 400,
  fontSize: "17px",
  textDecoration: "none",
  textAlign: "left",
  width: "100%",
  boxSizing: "border-box",
  cursor: "pointer",
};

// S35 · F234 — the icon column. One size for every row in both states, so "identical icons" is a
// property of the constant rather than of seven call sites agreeing.
const AUTH_MENU_ICON = {
  size: 22,
  strokeWidth: 1.5,
  color: "#4a4a4a",
} as const;

// exactly what the signed-in login-popup panel used to render (Signed in as … + the ACCOUNT_LINKS link-out
// rows + SIGN OUT) — only the presentation changed, per the founder re-walk. The `data-auth-signedin` and
// `data-account-link` hooks are preserved so the existing chrome/account-links-out check keeps asserting the
// same contract. Anchors to the clicked profile trigger; falls back to the top-right when no anchor is known.
function AccountMenu({
  anchor,
  email,
  onClose,
  onSignOut,
}: {
  anchor: { x: number; y: number } | null;
  email: string | null;
  onClose: () => void;
  onSignOut: () => void;
}) {
  // live greets by FIRST NAME ("Hi Joktan"). The chrome session carries only the email, so the
  // local-part stands in for it — a stated limitation, not a fabricated name. When the profile record
  // is available here it should replace this.
  const firstName = (email?.split("@")[0] ?? "there")
    .replace(/[._-]+/g, " ")
    .split(" ")[0];
  // F127 — some menu items open a dialog instead of navigating (live behaviour).
  const [modal, setModal] = useState<"agent" | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const TEAL = "#71abb8";
  const BORDER = "#cbe0e4";
  const GREY = "#828282";
  const ORANGE = "#f15a22";
  const WIDTH = 361; // F234 — the mockup's measured panel width (723px on the 2× crop)
  // Guard the viewport dimensions: embedded/headless contexts can report 0, and clamping against 0 would
  // slam the menu to the left edge (the RW-15(d) lesson).
  const vw =
    (typeof window !== "undefined" &&
      (window.innerWidth || document.documentElement?.clientWidth)) ||
    0;
  // Right-align the menu to the trigger (profile icon sits top-right). With no usable anchor, fall back to
  // the top-right corner — never the top-left, which would read as a stray panel.
  const fallbackLeft = vw > 0 ? vw - WIDTH - 16 : 16;
  const right = anchor ? anchor.x - WIDTH : fallbackLeft;
  const left =
    vw > 0 ? Math.max(8, Math.min(right, vw - WIDTH - 8)) : Math.max(8, right);
  const top = anchor ? anchor.y + 8 : 64;

  return (
    <div
      data-auth-overlay
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "transparent",
      }}
    >
      <div
        data-auth-signedin
        data-auth-menu
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          left,
          top,
          width: WIDTH,
          background: "#fdfdfd",
          // F234 — the mockup's panel is a soft card, not the teal-bordered popup. Sections own their
          // own padding now (header / rows / footer are separated by rules, not by one outer inset).
          border: `1px solid ${DIVIDER}`,
          borderRadius: "12px",
          boxShadow: "0 12px 28px rgba(0,0,0,.18)",
          fontFamily: '"Roboto", system-ui, sans-serif',
        }}
      >
        {/* F234 — the caret the mockup draws, pointing back up at the avatar trigger it hangs from */}
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: -8,
            right: 22,
            width: 0,
            height: 0,
            borderLeft: "9px solid transparent",
            borderRight: "9px solid transparent",
            borderBottom: `9px solid #fdfdfd`,
            filter: "drop-shadow(0 -1px 0 #e6e6e6)",
          }}
        />
        {/* ------------------------------------------------------------------------------------
            WO-GF-001-P1B-S16 · F119 — LIVE'S SIGNED-IN MENU, built to
            `LIVE-ACCOUNT-DROPDOWN-REFERENCE-2026-07-23.md` (founder screenshot; the ONLY permissible
            observation of this view — item 2 of the WO asked me to capture it from live signed-in,
            which the no-live-logins rail forbids).

            This is the founder's SECOND raise of the menu. The first fix (F109) repaired two dead
            routes; the ITEMS themselves were still ours, not live's. Live's set is 7 items + an
            About/Contact footer + Log Out bottom-right, greeting by FIRST NAME.

            ⚠ A SCREENSHOT SHOWS LABELS, NOT hrefs. Under the standing rule that destinations are read
            from real anchors and never invented, every target below is traced to something already
            real in this build (see the reference's §3 table) — and the two that have no honest target
            render INERT rather than pointing somewhere plausible:
              • Saved Items — this build has no such surface.
              • Buy & Sell  — live serves it dead too (RW2-8, founder-ratified), so inert IS faithful.
            ------------------------------------------------------------------------------------ */}
        {/* S35 · F234 — the mockup's header: avatar · "Hi <firstName>" · View Account · a boxed ✕.
            The avatar is the ALREADY-PORTED chrome asset (the same profileIcon the header trigger
            paints), not a new illustration — the mockup draws the trigger's own face here. */}
        <div
          data-account-menu-header
          style={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            padding: "18px 18px 16px",
          }}
        >
          <MemberAvatarImg alt="" width={51} height={51} fallback="/_next/static/media/profileIcon.4bf36b73.svg" style={{ flex: "0 0 auto", borderRadius: "50%" }} />
          <span style={{ flex: 1, minWidth: 0 }}>
            <strong
              style={{
                display: "block",
                fontSize: "20px",
                color: "#222",
                lineHeight: 1.25,
              }}
            >
              Hi {firstName}
            </strong>
            <a
              data-account-link
              href={accountsUrl("/account")}
              style={{ fontSize: "16px", color: GREY, textDecoration: "none" }}
            >
              View Account
            </a>
          </span>
          <button
            type="button"
            data-account-menu-close
            aria-label="Close"
            onClick={onClose}
            style={{
              flex: "0 0 auto",
              width: 26,
              height: 26,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              borderRadius: "6px",
              border: `1px solid #cfcfcf`,
              background: "#fff",
              color: "#4a4a4a",
              cursor: "pointer",
            }}
          >
            <XIcon size={16} strokeWidth={1.75} />
          </button>
        </div>
        <div style={{ height: 1, background: DIVIDER }} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "10px 6px",
          }}
        >
          {MENU_ITEMS.map((it) => {
            const Icon = it.icon;
            const glyph = (
              <Icon
                {...AUTH_MENU_ICON}
                style={{ flex: "0 0 auto" }}
                aria-hidden
              />
            );
            return it.modal ? (
              <button
                key={it.label}
                type="button"
                data-account-menu-item={it.label}
                data-auth-menu-row
                data-account-menu-modal={it.modal}
                onClick={() => setModal(it.modal ?? null)}
                style={{ ...AUTH_MENU_ROW, cursor: "pointer" }}
              >
                {glyph}
                {it.label}
              </button>
            ) : it.href ? (
              <a
                key={it.label}
                data-account-menu-item={it.label}
                data-auth-menu-row
                {...(it.external ? {} : { "data-account-link": "" })}
                href={it.href}
                {...(it.external
                  ? { target: "_blank", rel: "noreferrer" }
                  : {})}
                style={AUTH_MENU_ROW}
              >
                {glyph}
                {it.label}
              </a>
            ) : (
              // NO DESTINATION. FOUNDER RULING 2026-07-28: "rows without an existing destination
              // render with a routed TODO flag — no invented routes." So the row keeps its icon and
              // its place in the mockup's order, stays deliberately non-interactive, and SAYS that
              // it is unrouted instead of looking merely disabled. Saved Items has no surface in
              // this build; Buy & Sell is dead on live too (RW2-8, founder-ratified).
              <span
                key={it.label}
                data-account-menu-item={it.label}
                data-auth-menu-row
                data-account-menu-inert
                data-account-menu-todo
                style={{
                  ...AUTH_MENU_ROW,
                  color: "#b6b6b6",
                  cursor: "default",
                }}
              >
                {glyph}
                {it.label}
                <em
                  data-account-menu-todo-flag
                  title="No destination exists in this build yet — routed, not invented"
                  style={{
                    marginLeft: "auto",
                    flex: "0 0 auto",
                    fontStyle: "normal",
                    fontSize: "10px",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                    color: "#a08b7a",
                    background: "#f6efe9",
                    border: "1px solid #e6d8cc",
                    borderRadius: "4px",
                    padding: "2px 6px",
                  }}
                >
                  TODO
                </em>
              </span>
            );
          })}
        </div>
        <div style={{ height: 1, background: DIVIDER, margin: "0 18px" }} />
        {/* the mockup puts About/Contact on one line and Log Out on its own line beneath, pushed right */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            padding: "14px 18px 0",
          }}
        >
          <a
            href="https://snowgroup.ca"
            target="_blank"
            rel="noreferrer"
            data-account-menu-item="About"
            style={{ fontSize: "15px", color: GREY, textDecoration: "none" }}
          >
            About
          </a>
          <a
            href="/contact-us"
            data-account-menu-item="Contact"
            style={{ fontSize: "15px", color: GREY, textDecoration: "none" }}
          >
            Contact
          </a>
        </div>
        <div style={{ display: "flex", padding: "10px 18px 18px" }}>
          <button
            type="button"
            data-auth-signout
            data-auth-menu-row
            onClick={onSignOut}
            style={{
              marginLeft: "auto",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              border: "none",
              background: "none",
              color: GREY,
              fontSize: "16px",
              cursor: "pointer",
              padding: 0,
            }}
          >
            Log Out
            <LogOut size={20} strokeWidth={1.5} aria-hidden />
          </button>
        </div>
      </div>
      {modal === "agent" && (
        <AgentPartnerModal onClose={() => setModal(null)} />
      )}
    </div>
  );
}

// WO-GF-001-S23 · F147 — the SIGN-UP form. Fields + validation follow the FastCurve Techno-Functional
// "Signup" contract (First Name · Last Name · Email · Phone [for transport notifications] · Password ·
// Confirm Password; password rule: the shared length-only policy in packages/ui signup-funnel.ts). Submits through
// cart-store.signUp → the local Strapi register endpoint; on success the user is signed in and closed.
//
// ── CARD 86bb671n4 (split 1/2 of 86bb597gj) — THE SINGLE BOX IS NOW A FUNNEL ──────────────────────
// What was here until this card: ONE form carrying all six fields at once — the "current single-box
// flow" the card names. It is now three steps, in the card's own order:
//     credentials  →  account type  →  details
// The field CONTENT of each step is the render's (FIGMA-REFS-S36/sign-in-up.png); the ORDER is the
// card's, which differs from the render's two-step layout on purpose — see the note on SIGNUP_STEPS
// in `@repo/ui/lib/signup-funnel`, where the step machine, the field rules and the tier taxonomy all
// live exactly once so this popup and the accounts app's /sign-up page cannot drift apart.
//
// ⚠ THE TIER LIST IS PENDING-FOUNDER and is rendered from data, never from markup: every option below
// comes out of SIGNUP_ACCOUNT_TYPES. When the founder's member-types image lands, that array is
// replaced and this component does not change.
//
// ⚠ NO INVENTED PERSISTENCE. The account-type/tier selections are collected and OFFERED on `onProfile`,
// which NO call site passes on either surface — so today they are discarded at the end of the funnel.
// That is the correct behaviour and the hook is the seam for when it stops being; the earlier wording
// here said they were "handed to `onProfile`", which read as though something received them. Nothing
// does. They are NOT sent to the backend — `register-customer` has no field for them and inventing one
// against a taxonomy the founder has not ruled on would be exactly the fabricated-data defect this
// estate keeps paying for. What is submitted is the same five real fields as before. Routed, in the
// close-out, as "BE needs a member-profile field once the taxonomy is ruled".
function SignupFunnel({
  onCancel,
  onSubmit,
  onCreated,
  createdEmail,
  onDone,
  onMembershipInfo,
  onProfile,
}: {
  onCancel: () => void;
  onSubmit: (f: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
  }) => Promise<{ ok: boolean; error?: string }>;
  /** Hands the parent the address of an account that really exists. */
  onCreated?: (email: string) => void;
  /** The parent's copy of that address. Survives this component being remounted mid-submit. */
  createdEmail?: string | null;
  onDone: () => void;
  onMembershipInfo: () => void;
  onProfile?: (p: SignupProfileSelection) => void;
}) {
  const ORANGE = "#f15a22";
  const GREY = "#828282";
  const BORDER = "#cbe0e4";
  const [step, setStep] = useState<SignupStep>(SIGNUP_STEPS[0]);
  const [credentials, setCredentials] = useState<SignupCredentials>({
    email: "",
    password: "",
    confirm: "",
  });
  const [profile, setProfile] = useState<SignupProfileSelection>(
    emptyProfileSelection,
  );
  const [details, setDetails] = useState<SignupDetails>({
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The confirmation panel. Holds the email the visitor TYPED — `cart-store.signUp` returns only
  // `{ok, error}`, so there is no echoed-back value to show and this does not claim to be one. What
  // makes it honest is the GATE, not the source: it is set only after an ok response (and `signUp`
  // treats a missing jwt as a failure), so no path congratulates a visitor on an account that was
  // never created.
  const [created, setCreated] = useState<string | null>(null);

  const setCred =
    (k: keyof SignupCredentials) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setCredentials((s) => ({ ...s, [k]: e.target.value }));
  const setDetail =
    (k: keyof SignupDetails) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setDetails((s) => ({ ...s, [k]: e.target.value }));

  const toggleType = (id: string) =>
    setProfile((s) => ({
      ...s,
      types: s.types.includes(id)
        ? s.types.filter((t) => t !== id)
        : [...s.types, id],
    }));
  const toggleOption = (groupId: string, optionId: string) =>
    setProfile((s) => {
      const cur = s.options[groupId] ?? [];
      const next = cur.includes(optionId)
        ? cur.filter((o) => o !== optionId)
        : [...cur, optionId];
      return { ...s, options: { ...s.options, [groupId]: next } };
    });

  const canLeave = canLeaveStep(step, { credentials, profile, details });
  // Why Continue is refused, as sentences. Same verdict as `canLeave` (asserted in
  // signup-credentials.test.ts); this is what stops a disabled button being a silent one.
  const blockers = stepBlockers(step, { credentials, details });
  // Fields the visitor has already left — an empty-and-untouched field shows nothing until then.
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const touch = (k: string) => setTouched((t) => (t[k] ? t : { ...t, [k]: true }));
  const nextLabel = step === "details" ? "Sign Up" : "Continue";

  const goNext = () => {
    if (!canLeave) return;
    const n = nextSignupStep(step);
    if (n) setStep(n);
  };
  const goBack = () => {
    const p = prevSignupStep(step);
    if (p) setStep(p);
    else onCancel();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // The submit button only exists on the last step, but a keyboard Enter can fire a form submit from
    // any field, so the step is checked here too rather than trusted to the button's disabled state.
    if (step !== SIGNUP_STEPS[SIGNUP_STEPS.length - 1]) {
      goNext();
      return;
    }
    if (!canLeave) return;
    setBusy(true);
    setError(null);
    const r = await onSubmit({
      firstName: details.firstName.trim(),
      lastName: details.lastName.trim(),
      email: credentials.email.trim(),
      phone: details.phone.trim(),
      password: credentials.password,
    });
    setBusy(false);
    if (r.ok) {
      onProfile?.(profile);
      // Before setCreated: the parent must stop swapping this popup for the account menu (the session
      // is already set by now) or the state below lands on an unmounted component.
      onCreated?.(credentials.email.trim());
      setCreated(credentials.email.trim());
    } else setError(r.error || "Sign-up failed");
  };

  const field: React.CSSProperties = {
    width: "100%",
    height: "46px",
    padding: "0 14px",
    borderRadius: "6px",
    border: `1.5px solid ${BORDER}`,
    fontSize: "13px",
    boxSizing: "border-box",
    outline: "none",
    marginBottom: "10px",
    fontFamily: "inherit",
  };
  const checkRow: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "12px",
    color: "#333",
    padding: "3px 0",
    cursor: "pointer",
  };

  // ── CONFIRMATION POPUP ──────────────────────────────────────────────────────────────────────────
  // The render's funnel ends on DONE; this is what DONE lands on. Rendered only from a real success.
  //
  // `created` is this component's own copy and `createdEmail` is the parent's. The parent's is the one
  // that survives the remount described above; the local one covers the ordinary case where no remount
  // happened. Neither is set by anything but a resolved `{ ok: true }` from the register call.
  const confirmedEmail = created ?? createdEmail ?? null;
  if (confirmedEmail) {
    return (
      <div
        data-auth-signup-confirmation
        style={{ paddingTop: "28px", width: "100%", textAlign: "center" }}
      >
        <p
          style={{
            fontSize: "20px",
            fontWeight: 700,
            margin: "0 0 8px",
            color: "#333",
          }}
        >
          You&apos;re in the club!
        </p>
        <p style={{ fontSize: "13px", color: GREY, margin: "0 0 6px" }}>
          Your account is ready and you are signed in as
        </p>
        <p
          data-auth-signup-confirmation-email
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#333",
            margin: "0 0 18px",
            wordBreak: "break-all",
          }}
        >
          {confirmedEmail}
        </p>
        <button
          type="button"
          data-auth-signup-confirmation-info
          onClick={onMembershipInfo}
          style={{
            display: "block",
            width: "100%",
            padding: "11px",
            marginBottom: "10px",
            borderRadius: "6px",
            border: `1.5px solid ${ORANGE}`,
            background: "#fff",
            color: ORANGE,
            fontWeight: 600,
            fontSize: "14px",
            cursor: "pointer",
          }}
        >
          What&apos;s included?
        </button>
        <button
          type="button"
          data-auth-signup-confirmation-done
          onClick={onDone}
          style={{
            display: "block",
            width: "100%",
            padding: "13px",
            borderRadius: "6px",
            border: 0,
            background: ORANGE,
            color: "#fff",
            fontWeight: 600,
            fontSize: "15px",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <form
      data-auth-signup-form
      data-auth-signup-step={step}
      onSubmit={submit}
      style={{ paddingTop: "28px", width: "100%" }}
    >
      {/* Progress rail — reads out of SIGNUP_STEPS so it can never disagree with the machine. */}
      <div
        data-auth-signup-rail
        style={{ display: "flex", gap: "6px", marginBottom: "14px" }}
      >
        {SIGNUP_STEPS.map((s, i) => {
          const done = i < signupStepIndex(step);
          const current = s === step;
          return (
            <div
              key={s}
              data-auth-signup-rail-step={s}
              data-current={current || undefined}
              style={{ flex: 1 }}
            >
              <div
                style={{
                  height: "4px",
                  borderRadius: "2px",
                  background: current || done ? ORANGE : BORDER,
                }}
              />
              <div
                style={{
                  fontSize: "10px",
                  marginTop: "5px",
                  color: current ? "#333" : GREY,
                  fontWeight: current ? 600 : 400,
                }}
              >
                {SIGNUP_STEP_LABELS[s]}
              </div>
            </div>
          );
        })}
      </div>

      {step === "credentials" && (
        <div data-auth-signup-panel="credentials">
          {/* The render's "Join Now, it's Free! ⓘ" — the ⓘ is the membership-info trigger. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              marginBottom: "4px",
            }}
          >
            <span style={{ fontSize: "15px", fontWeight: 700, color: "#333" }}>
              Join Now, it&apos;s Free!
            </span>
            <button
              type="button"
              data-auth-membership-info
              aria-label="What is SnowClub membership?"
              onClick={onMembershipInfo}
              style={{
                width: "18px",
                height: "18px",
                lineHeight: "16px",
                borderRadius: "50%",
                border: `1.5px solid ${ORANGE}`,
                background: "#fff",
                color: ORANGE,
                fontSize: "11px",
                fontWeight: 700,
                cursor: "pointer",
                padding: 0,
              }}
            >
              i
            </button>
          </div>
          <p
            style={{
              textAlign: "center",
              fontSize: "11px",
              color: GREY,
              textTransform: "uppercase",
              margin: "0 0 16px",
            }}
          >
            Already a member?{" "}
            <button
              type="button"
              data-auth-signup-back
              onClick={onCancel}
              style={{
                border: 0,
                background: "none",
                color: ORANGE,
                fontWeight: 700,
                fontSize: "11px",
                cursor: "pointer",
                padding: 0,
                textTransform: "uppercase",
              }}
            >
              Log in
            </button>
          </p>
          <SignupCredentialsFields
            credentials={credentials}
            touched={touched}
            onChange={setCred}
            onBlur={touch}
            fieldStyle={field}
          />
        </div>
      )}

      {step === "account-type" && (
        <div data-auth-signup-panel="account-type">
          <p
            style={{
              textAlign: "center",
              fontSize: "15px",
              fontWeight: 700,
              color: "#333",
              margin: "0 0 12px",
            }}
          >
            {SIGNUP_TAXONOMY.heading}
          </p>
          {SIGNUP_ACCOUNT_TYPES.map((t) => {
            const picked = profile.types.includes(t.id);
            return (
              <div
                key={t.id}
                data-auth-signup-account-type={t.id}
                data-selected={picked || undefined}
                style={{
                  border: `1.5px solid ${picked ? ORANGE : BORDER}`,
                  borderRadius: "6px",
                  padding: "10px 12px",
                  marginBottom: "10px",
                }}
              >
                <label style={{ ...checkRow, alignItems: "flex-start" }}>
                  <input
                    type="checkbox"
                    checked={picked}
                    onChange={() => toggleType(t.id)}
                    style={{ marginTop: "3px", accentColor: ORANGE }}
                  />
                  <span>
                    <span
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: 700,
                        color: "#333",
                      }}
                    >
                      {t.title}
                    </span>
                    <span
                      style={{
                        display: "block",
                        fontSize: "11px",
                        color: GREY,
                      }}
                    >
                      {t.blurb}
                    </span>
                  </span>
                </label>
                {/* ALWAYS DRAWN — corrected 2026-07-31 (run 5) against the render itself, after an
                    adversarial review caught that this file and `signup-funnel.ts` made OPPOSITE claims
                    about it and only one could be true. Settled by opening
                    FIGMA-REFS-S36/sign-in-up.png, "Step 2 sign up": the Business Profile card's own
                    checkbox is EMPTY while its full nine-option list is drawn beside Personal's — and
                    two of those options (Hotel / Lodging, Travel Rep / Agent) are themselves ticked.
                    So the list does not belong to the card's tick, and gating it on `picked` (what this
                    file did until now) showed a visitor landing on step 2 two profile cards and ZERO
                    tiers. `signup-funnel.ts`'s docblock was the accurate one; this comment was not. */}
                <div
                  data-auth-signup-tiers={t.id}
                  style={{
                    borderTop: `1px solid ${BORDER}`,
                    marginTop: "8px",
                    paddingTop: "8px",
                  }}
                >
                    <p
                      style={{
                        fontSize: "10px",
                        color: GREY,
                        margin: "0 0 4px",
                      }}
                    >
                      {t.selectHint}
                    </p>
                    {/* Card 86bb671n4 — "marked clearly as PENDING-FOUNDER". The list below is
                        transcribed from a 2023 render, not a founder ruling, and the founder is
                        supplying the member-types image that replaces it. Marking it only in the
                        source would leave the one person walking this screen unable to tell which
                        part of it is settled. It sits on the LIST, not the step, because the step
                        order and the two profile cards ARE settled — and it reads its own words from
                        SIGNUP_TAXONOMY.status so removing the placeholder removes the notice. */}
                    {SIGNUP_TAXONOMY.status === "PENDING-FOUNDER" && (
                      <p
                        data-auth-signup-tiers-pending={t.id}
                        style={{
                          fontSize: "9px",
                          letterSpacing: "0.04em",
                          color: ORANGE,
                          margin: "0 0 6px",
                        }}
                      >
                        {SIGNUP_TAXONOMY.status} — placeholder list, awaiting the member types
                      </p>
                    )}
                    {t.options.map((o) => (
                      <label key={o.id} style={checkRow}>
                        <input
                          type="checkbox"
                          checked={(profile.options[t.id] ?? []).includes(o.id)}
                          onChange={() => toggleOption(t.id, o.id)}
                          style={{ accentColor: ORANGE }}
                        />
                        {o.label}
                      </label>
                    ))}
                    {(t.groups ?? []).map((g) => (
                      <div
                        key={g.id}
                        data-auth-signup-tier-group={g.id}
                        style={{ marginTop: "8px" }}
                      >
                        <p
                          style={{
                            fontSize: "10px",
                            color: GREY,
                            margin: "0 0 4px",
                          }}
                        >
                          {g.heading}
                        </p>
                        {g.options.map((o) => (
                          <label key={o.id} style={checkRow}>
                            <input
                              type="checkbox"
                              checked={(profile.options[g.id] ?? []).includes(
                                o.id,
                              )}
                              onChange={() => toggleOption(g.id, o.id)}
                              style={{ accentColor: ORANGE }}
                            />
                            {o.label}
                          </label>
                        ))}
                      </div>
                    ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {step === "details" && (
        <div data-auth-signup-panel="details">
          <SignupDetailsFields
            details={details}
            touched={touched}
            onChange={setDetail}
            onBlur={touch}
            fieldStyle={field}
          />
        </div>
      )}

      {error ? (
        <p
          data-auth-signup-error
          style={{ color: "#dc2626", fontSize: "13px", margin: "0 0 8px" }}
        >
          {error}
        </p>
      ) : null}

      {!busy && !canLeave ? (
        <SignupBlockedReason
          id="auth-signup-blocked"
          action={nextLabel}
          reasons={blockers}
        />
      ) : null}

      <button
        type="submit"
        data-auth-signup-next
        aria-describedby={!busy && !canLeave ? "auth-signup-blocked" : undefined}
        disabled={busy || !canLeave}
        style={{
          display: "block",
          width: "100%",
          padding: "13px",
          borderRadius: "6px",
          border: 0,
          background: ORANGE,
          color: "#fff",
          fontWeight: 600,
          fontSize: "15px",
          textTransform: "uppercase",
          cursor: busy || !canLeave ? "default" : "pointer",
          opacity: busy || !canLeave ? 0.6 : 1,
        }}
      >
        {busy ? "Creating account…" : nextLabel}
      </button>
      <button
        type="button"
        data-auth-signup-prev
        onClick={goBack}
        style={{
          display: "block",
          margin: "12px auto 0",
          border: "none",
          background: "none",
          color: GREY,
          fontSize: "13px",
          cursor: "pointer",
        }}
      >
        {prevSignupStep(step) ? "← Back" : "← Back to login"}
      </button>
    </form>
  );
}
